import type { ApiClient } from '../apiClient';
import type { SessionTokens } from './session';

export interface VerifyEmailOtpResult {
  verified: boolean;
  /**
   * Short-lived JWT returned ONLY when `verified` is true — proves this caller
   * (not merely someone who knows the email) just verified the OTP. Required by
   * enrollPasskey for FIRST-ENROLL (S6c 1b). Absent if verification failed.
   */
  enrollmentTicket?: string;
}

export function sendEmailOtp(
  api: ApiClient,
  email: string,
): Promise<{ otpId: string; expiresInSeconds: number }> {
  return api.post('/api/auth/otp/email/send', { channel: 'email', target: email });
}

export function verifyEmailOtp(
  api: ApiClient,
  email: string,
  code: string,
): Promise<VerifyEmailOtpResult> {
  return api.post<VerifyEmailOtpResult>('/api/auth/otp/email/verify', {
    channel: 'email',
    target: email,
    code,
  });
}

export function refreshSession(api: ApiClient, refreshToken: string): Promise<SessionTokens> {
  return api.post<SessionTokens>('/api/auth/login/refresh', { refreshToken });
}

/**
 * Password sign-in.
 *
 * A firm reaches its client list from whatever machine it is sitting at, so
 * this is the primary way in and the passkey is the shortcut on a device that
 * already has one — not the other way round.
 */
export function passwordLogin(
  api: ApiClient, email: string, password: string,
): Promise<SessionTokens> {
  return api.post('/api/auth/password/login', { email, password });
}

/** Ask for a set-or-reset link. Always resolves — the server refuses to say
 *  whether the address has an account, and the UI must not imply otherwise. */
export function requestPasswordLink(api: ApiClient, email: string): Promise<{ sent: true }> {
  return api.post('/api/auth/password/request', { email, app: 'advisor' });
}

/** Set a password from an emailed ticket; signs you in on success. */
export function setPassword(
  api: ApiClient, ticket: string, password: string,
): Promise<SessionTokens> {
  return api.post('/api/auth/password/set', { ticket, password });
}
